import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { createSelector, SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import {
  EmployeeStatusEnum,
  PermissionEnum,
} from '@electricjs/core_entity-client';

import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import {
  organizationApiSlice,
  useGetOrganizationEmployeesPaginatedQuery,
} from '@/redux/slices/organizationApiSlice';
import { RootState, useAppSelector } from '@/redux/store';
import { Employee } from '@/types/employees';
import { GetOrganizationEmployeesArgs } from '@/types/organizations';

/**
 * Every status the backend can return. The backend already scopes the list by
 * the caller's division access, so the result is every employee the current
 * user is allowed to see.
 *
 * Order matters: RTK Query keys its cache by serialized args, and
 * ShipLaptopPage passes these same statuses in this same order, so it shares
 * the cache entry.
 */
export const CENTRAL_EMPLOYEE_STATUSES: EmployeeStatusEnum[] = [
  EmployeeStatusEnum.Active,
  EmployeeStatusEnum.Inactive,
  EmployeeStatusEnum.Unknown,
];

/** Single source for the central query args, shared by the query and the selector. */
export const getCentralEmployeesArgs = (
  organizationId: string
): GetOrganizationEmployeesArgs => ({
  organizationId,
  status: CENTRAL_EMPLOYEE_STATUSES,
});

const EMPTY_EMPLOYEES: Employee[] = [];
const EMPTY_EMPLOYEES_BY_ID: Record<string, Employee> = {};

/**
 * Builds the id → employee map once per change in the cached data, so every
 * consumer shares the same memoized object.
 */
export const selectCentralEmployeesById = createSelector(
  [
    (state: RootState, organizationId: string) =>
      organizationApiSlice.endpoints.getOrganizationEmployeesPaginated.select(
        getCentralEmployeesArgs(organizationId)
      )(state).data?.results,
  ],
  (employees): Record<string, Employee> =>
    employees
      ? Object.fromEntries(employees.map(employee => [employee.id, employee]))
      : EMPTY_EMPLOYEES_BY_ID
);

export type EmployeesMap = {
  /**
   * False when the flag is off, or the user can't list employees: consumers
   * must keep using their own payloads.
   */
  isEnabled: boolean;
  /** The organization the map holds employees for. */
  organizationId: string;
  employees: Employee[];
  employeesById: Record<string, Employee>;
  /** Undefined means the current user can't see this employee. */
  getEmployeeById: (id?: string | null) => Employee | undefined;
  /** First load only. Background refetches don't set it. */
  isLoading: boolean;
  /** True once the central query has returned a list at least once. */
  hasData: boolean;
  error?: FetchBaseQueryError | SerializedError;
  /** Retries the central query. Does nothing when the flag is off. */
  refetch: () => void;
};

/**
 * Centralized, division-scoped employee map.
 *
 * If an employee isn't in this map, the current user can't see them.
 */
export const useEmployeesMap = (): EmployeesMap => {
  const { ithDivisions } = useFlags();
  // Listing employees needs employees:read, which normal employees (Hub User)
  // don't have. Without it the request would always fail with a 403.
  const canListEmployees = useUserHasOrganizationPermission(
    PermissionEnum.Employeesread
  );
  const isEnabled = Boolean(ithDivisions) && canListEmployees;
  const organizationId = useGetOrganizationId();

  // currentData, not data: when the organization changes, RTK Query keeps
  // returning the previous organization's data in `data` until the new request
  // finishes, which would expose another org's employees.
  const {
    currentData,
    isFetching,
    error,
    refetch: refetchQuery,
  } = useGetOrganizationEmployeesPaginatedQuery(
    getCentralEmployeesArgs(organizationId),
    { skip: !isEnabled }
  );

  const cachedEmployeesById = useAppSelector(state =>
    selectCentralEmployeesById(state, organizationId)
  );
  // The cache entry can exist with the flag off (ShipLaptopPage uses the same
  // args), so don't expose it unless the map is enabled.
  const employeesById = isEnabled ? cachedEmployeesById : EMPTY_EMPLOYEES_BY_ID;

  const getEmployeeById = useCallback(
    (id?: string | null) =>
      id && Object.hasOwn(employeesById, id) ? employeesById[id] : undefined,
    [employeesById]
  );

  // RTK Query throws if a skipped query is refetched.
  const refetch = useCallback(() => {
    if (isEnabled) {
      refetchQuery();
    }
  }, [isEnabled, refetchQuery]);

  return {
    isEnabled,
    organizationId,
    employees: (isEnabled && currentData?.results) || EMPTY_EMPLOYEES,
    employeesById,
    getEmployeeById,
    isLoading: isEnabled && isFetching && currentData === undefined,
    hasData: isEnabled && currentData !== undefined,
    error: isEnabled
      ? (error as FetchBaseQueryError | SerializedError | undefined)
      : undefined,
    refetch,
  };
};
